/**
 * AlertsSection component
 * Displays warnings and alerts for the end-of-day summary:
 * - Low yield sessions
 * - High void rate sessions
 * - Incomplete sessions
 * - By-product aging alerts (with dismiss capability)
 * With dark mode support
 */
import React, { useState } from 'react';
import { useLanguage } from '../../../../i18n/LanguageContext';
import { SummaryAlert } from '../../../../services/productionService';
import {
  AlertTriangle,
  Info,
  XCircle,
  Clock,
  Check,
} from 'lucide-react';

interface AlertsSectionProps {
  alerts: SummaryAlert[];
  onSessionClick?: (sessionId: string) => void;
  onDismissAlert?: (alertKey: string) => Promise<void>;
  canDismissFreshnessAlerts?: boolean;
}

const AlertsSection: React.FC<AlertsSectionProps> = ({
  alerts,
  onSessionClick,
  onDismissAlert,
  canDismissFreshnessAlerts = false,
}) => {
  const { t } = useLanguage();
  const [dismissingAlerts, setDismissingAlerts] = useState<Set<string>>(new Set());
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<string>>(new Set());

  const handleDismiss = async (alertKey: string) => {
    if (!onDismissAlert || dismissingAlerts.has(alertKey)) return;
    setDismissingAlerts(prev => new Set(prev).add(alertKey));
    try {
      await onDismissAlert(alertKey);
      setDismissedAlerts(prev => new Set(prev).add(alertKey));
    } catch (error) {
      console.error('Failed to dismiss alert in UI:', error);
    } finally {
      setDismissingAlerts(prev => {
        const next = new Set(prev);
        next.delete(alertKey);
        return next;
      });
    }
  };

  // Look up the localized template and substitute params. When the key is
  // missing (t returns the key unchanged), fall back to the backend message.
  const localizeAlert = (key: string, message: string, subs: Record<string, string>): string => {
    const template = t(key);
    if (template === key) return message;
    return Object.entries(subs).reduce((s, [ph, val]) => s.replace(ph, val), template);
  };
  const buildAlertMessage = (alert: SummaryAlert): string => {
    switch (alert.type) {
      case 'low_yield':
        return localizeAlert('endOfDaySummary.alertLowYield', alert.message, {
          '{{session}}': alert.session_number ?? '',
          '{{pct}}': String(alert.yield_percentage ?? ''),
        });
      case 'high_void_rate':
        return localizeAlert('endOfDaySummary.alertHighVoidRate', alert.message, {
          '{{session}}': alert.session_number ?? '',
          '{{pct}}': String(alert.void_rate ?? ''),
        });
      case 'incomplete_sessions':
        return localizeAlert('endOfDaySummary.alertIncompleteSessions', alert.message, {
          '{{count}}': String(alert.count ?? alert.session_numbers?.length ?? ''),
        });
      case 'byproduct_aging':
        return localizeAlert('endOfDaySummary.alertByproductAging', alert.message, {
          '{{item}}': alert.item_name ?? alert.item_number ?? '',
          '{{lbs}}': String(alert.current_amount ?? ''),
          '{{days}}': String(alert.age_days ?? ''),
        });
      default:
        return alert.message;
    }
  };

  if (alerts.length === 0) {
    return (
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center gap-3">
        <div className="bg-green-500 p-2 rounded-full">
          <Info className="h-5 w-5 text-white" />
        </div>
        <p className="text-green-700 dark:text-green-400 font-medium">{t('endOfDaySummary.noAlerts')}</p>
      </div>
    );
  }

  const getAlertIcon = (severity: string, type?: string) => {
    // Special icon for by-product aging
    if (type === 'byproduct_aging') {
      return <Clock className="h-5 w-5 text-white" />;
    }
    switch (severity) {
      case 'error':
        return <XCircle className="h-5 w-5 text-white" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-white" />;
      default:
        return <Info className="h-5 w-5 text-white" />;
    }
  };

  const getAlertColors = (severity: string) => {
    switch (severity) {
      case 'error':
        return {
          bg: 'bg-red-50 dark:bg-red-900/20',
          border: 'border-red-200 dark:border-red-800',
          iconBg: 'bg-red-500',
          text: 'text-red-700 dark:text-red-400',
        };
      case 'warning':
        return {
          bg: 'bg-yellow-50 dark:bg-yellow-900/20',
          border: 'border-yellow-200 dark:border-yellow-800',
          iconBg: 'bg-yellow-500',
          text: 'text-yellow-700 dark:text-yellow-400',
        };
      default:
        return {
          bg: 'bg-blue-50 dark:bg-blue-900/20',
          border: 'border-blue-200 dark:border-blue-800',
          iconBg: 'bg-blue-500',
          text: 'text-blue-700 dark:text-blue-400',
        };
    }
  };

  // Filter out dismissed alerts
  const visibleAlerts = alerts.filter(alert => {
    if (alert.type === 'byproduct_aging' && alert.lot_id) {
      const alertKey = `freshness:lot:${alert.lot_id}`;
      return !dismissedAlerts.has(alertKey);
    }
    return true;
  });

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-yellow-500" />
        {t('endOfDaySummary.alerts')} ({visibleAlerts.length})
      </h3>
      <div className="space-y-2">
        {visibleAlerts.map((alert, index) => {
          const colors = getAlertColors(alert.severity);
          const alertKey = alert.type === 'byproduct_aging' && alert.lot_id
            ? `freshness:lot:${alert.lot_id}`
            : null;
          const isDismissing = alertKey ? dismissingAlerts.has(alertKey) : false;

          return (
            <div
              key={index}
              className={`${colors.bg} ${colors.border} border rounded-lg p-3 flex items-center gap-3`}
            >
              <div className={`${colors.iconBg} p-1.5 rounded-full flex-shrink-0`}>
                {getAlertIcon(alert.severity, alert.type)}
              </div>
              <div className="flex-grow">
                <p className={`${colors.text} text-sm`}>{buildAlertMessage(alert)}</p>
                {alert.type === 'byproduct_aging' && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {t('freshness.alertByProductAging')}
                  </p>
                )}
              </div>
              {alert.session_number && onSessionClick && (
                <button
                  onClick={() => onSessionClick(alert.session_number!)}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline flex-shrink-0"
                >
                  {t('endOfDaySummary.viewSession')}
                </button>
              )}
              {alert.type === 'byproduct_aging' && alertKey && onDismissAlert && canDismissFreshnessAlerts && (
                <button
                  onClick={() => handleDismiss(alertKey)}
                  disabled={isDismissing}
                  className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 flex items-center gap-1 flex-shrink-0 disabled:opacity-50"
                >
                  {isDismissing ? (
                    <span className="animate-pulse">{t('common.loading')}</span>
                  ) : (
                    <>
                      <Check className="h-3 w-3" />
                      {t('freshness.resolveForToday')}
                    </>
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AlertsSection;
